import { useMemo } from 'react';
import {
  calculateSharedAggregates,
  calculateSharedMetrics,
  calculateSharedTopInfluencers,
} from '../services/sharedAnalyticsCalculations.service';
import { calculateTopPosts } from '../services/analyticsCalculations.service';
import type { PublicDeliverableAggregate, SharedMetricsData } from '../types/sharedAnalytics.types';
import type { TopInfluencer } from '../types/analytics.types';
import type { TopPost } from '@/hooks/useTopPerformingPosts';

export interface UseSharedAnalyticsCalculationsProps {
  aggregates: PublicDeliverableAggregate[];
  summaryData?: {
    cpv?: number | null;
    cpe?: number | null;
  } | null;
  posts: TopPost[];
  showAllInfluencers: boolean;
  showAllPosts: boolean;
}

export function useSharedAnalyticsCalculations({
  aggregates,
  summaryData,
  posts,
  showAllInfluencers,
  showAllPosts,
}: UseSharedAnalyticsCalculationsProps) {
  const rawAggregates = useMemo(
    () => calculateSharedAggregates(aggregates),
    [aggregates]
  );

  const metrics: SharedMetricsData = useMemo(
    () => calculateSharedMetrics(rawAggregates, summaryData),
    [rawAggregates, summaryData]
  );

  const topInfluencers: TopInfluencer[] = useMemo(
    () => calculateSharedTopInfluencers(aggregates, showAllInfluencers),
    [aggregates, showAllInfluencers]
  );

  const followerMap = useMemo(() => {
    const map = new Map<string, number>();
    for (const agg of aggregates) {
      if (agg.influencer_name && agg.influencer_followers) {
        map.set(agg.influencer_name.toLowerCase().trim(), agg.influencer_followers);
      }
    }
    return map;
  }, [aggregates]);

  const enrichedPosts: TopPost[] = useMemo(() => {
    return posts.map((post) => {
      let erFollowers = post.erFollowers;
      if (erFollowers === null || erFollowers === undefined) {
        const followers = followerMap.get(post.influencerName?.toLowerCase().trim() || '');
        if (followers && followers > 0) {
          erFollowers = (((post.likes || 0) + (post.comments || 0)) / followers) * 100;
        }
      }

      const erViews =
        post.erViews !== null && post.erViews !== undefined
          ? post.erViews
          : post.views && post.views > 0
            ? (post.engagementRate || (((post.likes || 0) + (post.comments || 0) + (post.shares || 0)) / post.views) * 100)
            : null;

      return {
        ...post,
        erFollowers: erFollowers ?? null,
        erViews: erViews ?? null,
      };
    });
  }, [posts, followerMap]);

  const topPosts: TopPost[] = useMemo(
    () => calculateTopPosts(enrichedPosts, showAllPosts),
    [enrichedPosts, showAllPosts]
  );

  return {
    metrics,
    topInfluencers,
    topPosts,
  };
}
